import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import { usePricingPlans } from "../../hooks/usePricingPlans";
import { createCheckoutSession } from "../../lib/paymentsApi";
import {
    HoneypotField,
    createFormSecurityState,
    withFormSecurity,
} from "../../lib/formSecurity";

const EMPTY_FORM = {
    name: "",
    email: "",
    company: "",
    planId: "starter",
    licenseCount: 10,
    phone: "",
    message: "",
};

export const PricingPurchaseDialog = ({
    open,
    onOpenChange,
    tier,
    yearly,
    onContactSales,
}) => {
    const navigate = useNavigate();
    const pricing = usePricingPlans();
    const {
        tiers,
        getTierById,
        getTierLicenseBounds,
        clampLicenseCount,
        calculatePricingTotal,
        formatPrice,
        isEnterpriseTier,
    } = pricing;

    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [security, setSecurity] = useState(createFormSecurityState);

    useEffect(() => {
        if (!open || !tier?.id) return;
        const bounds = getTierLicenseBounds(tier.id);
        setForm({
            ...EMPTY_FORM,
            planId: tier.id,
            licenseCount: bounds.min,
        });
        setSecurity(createFormSecurityState());
    }, [open, tier?.id, getTierLicenseBounds]);

    const activeTier = getTierById(form.planId);
    const bounds = getTierLicenseBounds(activeTier.id);
    const licenseCount = clampLicenseCount(activeTier.id, form.licenseCount);
    const quote = useMemo(
        () =>
            activeTier.monthly === null
                ? null
                : calculatePricingTotal(activeTier.id, licenseCount, yearly),
        [activeTier.id, activeTier.monthly, licenseCount, yearly, calculatePricingTotal],
    );
    const billingLabel = yearly ? "Yearly (−20%)" : "Monthly";
    const isEnterprise = isEnterpriseTier(activeTier);

    const changePlan = (planId) => {
        const nextBounds = getTierLicenseBounds(planId);
        setForm((prev) => ({
            ...prev,
            planId,
            licenseCount: clampLicenseCount(
                planId,
                prev.licenseCount || nextBounds.min,
            ),
        }));
    };

    const setLicenseCount = (value) => {
        setForm((prev) => ({
            ...prev,
            licenseCount: clampLicenseCount(activeTier.id, value),
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.company) {
            toast.error("Please fill name, work email and company.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("That email doesn’t look right.");
            return;
        }

        if (isEnterprise) {
            onOpenChange(false);
            if (onContactSales) {
                onContactSales();
            } else {
                navigate("/contact-sales");
            }
            return;
        }

        if (!quote) {
            toast.error("Unable to calculate pricing for this plan.");
            return;
        }

        setSubmitting(true);
        try {
            const session = await createCheckoutSession(withFormSecurity({
                planId: activeTier.id,
                licenseCount: quote.licenseCount,
                yearly,
                name: form.name,
                email: form.email,
                company: form.company,
                phone: form.phone,
                message: form.message,
            }, security));

            if (session.checkout_mode === "form_post" && session.payu?.action && session.payu?.fields) {
                const form = document.createElement("form");
                form.method = "POST";
                form.action = session.payu.action;
                form.style.display = "none";
                Object.entries(session.payu.fields).forEach(([name, value]) => {
                    const input = document.createElement("input");
                    input.type = "hidden";
                    input.name = name;
                    input.value = value == null ? "" : String(value);
                    form.appendChild(input);
                });
                document.body.appendChild(form);
                form.submit();
                return;
            }

            if (!session.checkout_url) {
                throw new Error("Checkout URL was not returned.");
            }

            window.location.href = session.checkout_url;
        } catch (err) {
            toast.error(
                err.message ??
                    "Could not start checkout. Try again or contact sales.",
            );
            setSubmitting(false);
        }
    };

    if (!tier) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-lg border-white/10 bg-obsidian-200 text-white sm:rounded-2xl"
                data-testid="pricing-purchase-dialog"
            >
                <DialogHeader>
                    <p className="font-code text-[10px] uppercase tracking-[0.2em] text-electric">
                        // {activeTier.name.toLowerCase()} ·{" "}
                        {billingLabel.toLowerCase()}
                    </p>
                    <DialogTitle className="font-display text-xl text-white">
                        {isEnterprise ? "Contact sales" : "Complete purchase"}
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400 font-light">
                        {activeTier.licenses} · Select licenses and proceed to checkout.
                    </DialogDescription>
                </DialogHeader>

                <form
                    onSubmit={submit}
                    className="space-y-4 mt-2 relative"
                    data-testid="pricing-purchase-form"
                >
                    <HoneypotField
                        value={security._hp}
                        onChange={(v) => setSecurity((s) => ({ ...s, _hp: v }))}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field
                            label="Your name"
                            testId="pricing-form-name"
                            value={form.name}
                            onChange={(v) => setForm({ ...form, name: v })}
                            placeholder="Aanya Mehta"
                        />
                        <Field
                            label="Work email"
                            testId="pricing-form-email"
                            type="email"
                            value={form.email}
                            onChange={(v) => setForm({ ...form, email: v })}
                            placeholder="aanya@company.com"
                        />
                        <Field
                            label="Company"
                            testId="pricing-form-company"
                            value={form.company}
                            onChange={(v) => setForm({ ...form, company: v })}
                            placeholder="Ledgerly"
                        />
                        <Field
                            label="Phone"
                            testId="pricing-form-phone"
                            type="tel"
                            value={form.phone}
                            onChange={(v) => setForm({ ...form, phone: v })}
                            placeholder="+1 555 0100"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 mb-2">
                                Plan
                            </label>
                            <select
                                data-testid="pricing-form-plan"
                                value={form.planId}
                                onChange={(e) => changePlan(e.target.value)}
                                className="w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 font-medium"
                            >
                                {tiers.map((t) => (
                                    <option
                                        key={t.id}
                                        value={t.id}
                                        className="bg-obsidian-300"
                                    >
                                        {t.name} · {t.licenses}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 mb-2">
                                Licenses
                            </label>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setLicenseCount(licenseCount - 1)
                                    }
                                    disabled={
                                        licenseCount <= bounds.min || submitting
                                    }
                                    className="shrink-0 w-10 h-10 rounded-lg border border-white/10 bg-white/[0.04] text-white disabled:opacity-30 hover:bg-white/[0.08] transition"
                                    aria-label="Decrease licenses"
                                >
                                    <Minus className="w-4 h-4 mx-auto" />
                                </button>
                                <input
                                    type="number"
                                    min={bounds.min}
                                    max={bounds.max ?? undefined}
                                    data-testid="pricing-form-licenses"
                                    value={licenseCount}
                                    onChange={(e) =>
                                        setLicenseCount(e.target.value)
                                    }
                                    className="w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 font-code text-center tabular-nums"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        setLicenseCount(licenseCount + 1)
                                    }
                                    disabled={
                                        (bounds.max !== null &&
                                            licenseCount >= bounds.max) ||
                                        submitting
                                    }
                                    className="shrink-0 w-10 h-10 rounded-lg border border-white/10 bg-white/[0.04] text-white disabled:opacity-30 hover:bg-white/[0.08] transition"
                                    aria-label="Increase licenses"
                                >
                                    <Plus className="w-4 h-4 mx-auto" />
                                </button>
                            </div>
                            <p className="mt-1.5 text-[10px] text-zinc-600 font-code">
                                {bounds.min}
                                {bounds.max ? ` – ${bounds.max}` : "+"} licenses
                            </p>
                        </div>
                    </div>

                    {quote && (
                        <div
                            className="rounded-xl border border-electric/20 bg-electric/5 p-4 space-y-2"
                            data-testid="pricing-payment-summary"
                        >
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-zinc-400">
                                    {quote.licenseCount} ×{" "}
                                    {formatPrice(quote.perLicense)} / license /{" "}
                                    {quote.billingPeriod}
                                </span>
                                <span className="text-zinc-500 font-code text-[10px] uppercase tracking-wider">
                                    {quote.label}
                                </span>
                            </div>
                            <div className="flex items-end justify-between gap-4">
                                <div>
                                    <p className="text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500">
                                        Total due today
                                    </p>
                                    <p className="font-display text-2xl font-semibold text-white tabular-nums">
                                        {formatPrice(quote.amount)}
                                    </p>
                                </div>
                                <p className="text-[11px] text-zinc-500 text-right max-w-[140px]">
                                    ≈ {formatPrice(quote.perLicenseMonthlyDisplay)}
                                    / license / mo
                                </p>
                            </div>
                        </div>
                    )}

                    {isEnterprise && (
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-zinc-400">
                            Enterprise pricing is custom. We&apos;ll route you to
                            sales to finalize license count and contract terms.
                        </div>
                    )}

                

                    <button
                        type="submit"
                        disabled={submitting}
                        className="group w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-6 py-3 rounded-full hover:brightness-110 transition disabled:opacity-60"
                        data-testid="pricing-form-submit"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Redirecting to payment…
                            </>
                        ) : isEnterprise ? (
                            <>
                                Contact sales
                                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                            </>
                        ) : (
                            <>
                                Proceed to Pay {quote ? formatPrice(quote.amount) : ""}
                                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                            </>
                        )}
                    </button>

                    <p className="text-[11px] text-zinc-600 font-code text-center">
                        Secure checkout hosted by Zoho Payments. You&apos;ll be
                        redirected to complete payment.
                    </p>
                </form>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({
    label,
    value,
    onChange,
    placeholder,
    type = "text",
    testId,
}) => (
    <div>
        <label className="block text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 mb-2">
            {label}
        </label>
        <input
            type={type}
            data-testid={testId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 placeholder:text-zinc-600"
        />
    </div>
);
